import { useMemo } from 'react';
import type { BoxProps } from '@withjoy/joykit';
import type { HeaderTitleStyle, NavigationStyle, WebsiteHeaderColors } from '@shared/types/WebsiteHeaderSettings.types';
import { getBackgroundEffectStyles } from '@shared/utils/headerBackgroundEffectStyles';

export interface UseHeaderStylesForGuestInput {
  headerTitleStyle?: HeaderTitleStyle;
  navigationStyle?: NavigationStyle;
  colorsData?: WebsiteHeaderColors;
}

export interface UseHeaderStylesForGuestResult {
  navTextColor: string;
  navEffectStyles: BoxProps;
  titleTextColor: string;
  titleEffectStyles: BoxProps;
  titleBackgroundColor?: string;
}

/**
 * Hook that computes header styles for guest site rendering.
 * Backfills effects from legacy colorsData when new style objects don't exist.
 *
 * Backfill logic:
 * - If colorsData.nameAndNavBackgroundColorEnabled: 'solid' effect with backgroundColor
 * - Otherwise: 'clear' effect
 * - Text color fallback chain: style.textColor → colorsData.nameAndNavFontColor → DEFAULT_HEADER_TEXT_COLOR
 *
 * The text-color fallback MUST be a hex string. Downstream consumers — notably
 * `getHoverColor` → `darkenColor` → chroma() (see `NavBar.tsx`) — parse the
 * value as hex and throw on design-system tokens like `'mono14'`. Layouts
 * without seeded colors (e.g. monogram + background) would otherwise crash
 * the whole header. `#333333` is the hex equivalent of the `mono14` token in
 * joykit's primitive palette.
 */
const DEFAULT_HEADER_TEXT_COLOR = '#333333';

export const useHeaderStylesForGuest = ({ headerTitleStyle, navigationStyle, colorsData }: UseHeaderStylesForGuestInput): UseHeaderStylesForGuestResult => {
  const navTextColor = useMemo(() => {
    return navigationStyle?.navigationTextColor || colorsData?.nameAndNavFontColor || DEFAULT_HEADER_TEXT_COLOR;
  }, [navigationStyle, colorsData]);

  const navEffectStyles = useMemo<BoxProps>(() => {
    if (navigationStyle) {
      return getBackgroundEffectStyles(navigationStyle.backgroundEffect, navigationStyle.backgroundColor);
    }

    // Backfill from legacy colors
    if (colorsData?.nameAndNavBackgroundColorEnabled && colorsData.nameAndNavBackgroundColor) {
      return getBackgroundEffectStyles('solid', colorsData.nameAndNavBackgroundColor);
    }

    return getBackgroundEffectStyles('clear');
  }, [navigationStyle, colorsData]);

  const titleTextColor = useMemo(() => {
    return headerTitleStyle?.headerTextColor || colorsData?.nameAndNavFontColor || DEFAULT_HEADER_TEXT_COLOR;
  }, [headerTitleStyle, colorsData]);

  const titleEffectStyles = useMemo<BoxProps>(() => {
    if (headerTitleStyle) {
      return getBackgroundEffectStyles(headerTitleStyle.backgroundEffect, headerTitleStyle.backgroundColor);
    }

    // Backfill from legacy colors
    if (colorsData?.nameAndNavBackgroundColorEnabled && colorsData.nameAndNavBackgroundColor) {
      return getBackgroundEffectStyles('solid', colorsData.nameAndNavBackgroundColor);
    }

    return getBackgroundEffectStyles('clear');
  }, [headerTitleStyle, colorsData]);

  return {
    navTextColor,
    navEffectStyles,
    titleTextColor,
    titleEffectStyles,
  };
};
